import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * The design's section heading: a title with a thick brand rule under it.
 *
 * Transcribed - weight 700 at -0.3px tracking, 10px of space, then a 2px
 * brand-coloured border, and `inline-block` so the rule stops at the end of
 * the words rather than running the width of the page.
 *
 * ── Why that detail matters ─────────────────────────────────────────────────
 *
 * A rule that stops with the text reads as an underline on a label. A rule that
 * crosses the page reads as a divider between two things, which is the opposite
 * of what a heading does - it groups what follows rather than separating it
 * from what came before. Every section on the admin and ops screens uses this,
 * and it is most of what makes those pages look composed rather than listed.
 *
 * ── The action slot ────────────────────────────────────────────────────────
 *
 * The design pairs several of these with a 34px round button on the far right -
 * "View all organizations", "View all critical items". It is optional, because
 * a heading with nowhere to go should not reserve the space.
 */
export interface SectionHeadingProps {
  children: ReactNode;
  /** Pushed to the right of the row. The design uses a round jump button. */
  action?: ReactNode;
  /**
   * 24px on the admin dashboard's own sections, 20px on the ops screens.
   * Defaults to the smaller, which is the one used more often.
   */
  size?: 'lg' | 'md';
  className?: string;
}

export function SectionHeading({ children, action, size = 'md', className }: SectionHeadingProps) {
  return (
    <div className={cn('mb-4 flex items-center justify-between gap-4', className)}>
      <h2
        className={cn(
          'inline-block border-b-2 border-primary pb-2.5 font-bold tracking-[-0.3px]',
          size === 'lg' ? 'text-2xl' : 'text-xl',
        )}
      >
        {children}
      </h2>
      {action}
    </div>
  );
}

/**
 * The round jump button the design pairs with a heading.
 *
 * A link rather than a button when it navigates, which is why `render` takes
 * the content instead of this taking an href: the shell stays router-agnostic,
 * as AppShell does, so each portal supplies its own link component.
 */
export function JumpButton({
  label,
  render,
  tone = 'light',
}: {
  /** Announced to a screen reader, and the tooltip on hover. */
  label: string;
  render: (content: ReactNode, className: string) => ReactNode;
  /** `dark` for use on the gradient banner, where a tinted ground is invisible. */
  tone?: 'light' | 'dark';
}) {
  /*
    The label rides inside the content rather than being a `title` on the
    control, because an arrow on its own announces as "link" and nothing else.
    `sr-only` keeps the design's bare circle while giving the control a name.
  */
  const content = (
    <>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
      <span className="sr-only">{label}</span>
    </>
  );

  return render(
    content,
    cn(
      'flex size-[34px] flex-none items-center justify-center rounded-full transition-colors',
      tone === 'dark'
        ? 'bg-white/20 text-white hover:bg-white/30'
        : 'bg-brand-50 text-brand-600 hover:bg-brand-100 dark:bg-brand-600/15 dark:text-brand-300 dark:hover:bg-brand-600/25',
    ),
  );
}
